/**
 * ChoirHymnsTab — Hymn library browser + service-hymn selection panel.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, Check, ChevronLeft, ChevronRight, BookOpen, X, Save, RefreshCw,
  Pencil, Trash2, Camera, Loader2,
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function ChoirHymnsTab({
  // Hymn list / search
  hymns,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  totalPages,
  // Add hymn form
  isMainAdmin,
  showHymnForm,
  setShowHymnForm,
  hymnDraft,
  setHymnDraft,
  onSaveHymn,
  // Service selection
  serviceTypes,
  serviceType,
  setServiceType,
  serviceDate,
  setServiceDate,
  selectedHymns,
  setSelectedHymns,
  requiredHymnCount,
  onToggleHymnSelection,
  onSaveHymnSelection,
  saving,
  // Parent callback — refresh hymns after edit/delete
  onHymnsChanged,
}) {
  // Inline edit state
  const [editingKey, setEditingKey] = useState(null); // `${number}-${language}`
  const [editDraft, setEditDraft] = useState({ title: '', first_line: '', verses: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const editFileInputRef = React.useRef(null);

  const runOcrOnImage = async (file) => {
    if (!file) return;
    setOcrError('');
    setOcrLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/choir/hymns/ocr`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const d = res.data || {};
      // Merge OCR into the edit draft, keeping any existing manual edits the
      // user would lose only if OCR found something.
      setEditDraft({
        title: d.title || '',
        first_line: d.first_line || '',
        verses: Array.isArray(d.verses) ? d.verses.join('\n\n') : '',
      });
    } catch (err) {
      console.error('OCR failed:', err);
      setOcrError(err.response?.data?.detail || 'Could not read the hymn from that photo. Try better lighting or a sharper shot.');
    } finally {
      setOcrLoading(false);
    }
  };

  const startEdit = (hymn) => {
    setEditingKey(`${hymn.number}-${hymn.language}`);
    setEditDraft({
      title: hymn.title || '',
      first_line: hymn.first_line || '',
      verses: (hymn.verses || []).join('\n\n'),
    });
    setOcrError('');
  };
  const cancelEdit = () => { setEditingKey(null); setEditDraft({ title: '', first_line: '', verses: '' }); setOcrError(''); };

  const saveEdit = async (hymn) => {
    if (!editDraft.title.trim()) {
      alert('Title is required');
      return;
    }
    setEditSaving(true);
    try {
      const verses = editDraft.verses.split('\n\n').map(v => v.trim()).filter(Boolean);
      await axios.put(`${API}/choir/hymns/${hymn.number}?language=${hymn.language}`, {
        title: editDraft.title,
        first_line: editDraft.first_line,
        verses,
      });
      cancelEdit();
      if (onHymnsChanged) onHymnsChanged();
    } catch (err) {
      console.error('Error updating hymn:', err);
      alert(err.response?.data?.detail || 'Error updating hymn');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteHymn = async (hymn) => {
    if (!window.confirm(`Delete Hymn #${hymn.number} (${hymn.language}) — "${hymn.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/choir/hymns/${hymn.number}?language=${hymn.language}`);
      if (onHymnsChanged) onHymnsChanged();
    } catch (err) {
      console.error('Error deleting hymn:', err);
      alert(err.response?.data?.detail || 'Error deleting hymn');
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Hymn Browser */}
      <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search hymns by number or title..."
                className="w-full pl-10 pr-4 py-2 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
            {isMainAdmin && (
              <button
                onClick={() => setShowHymnForm(!showHymnForm)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-100 text-green-700 font-medium hover:bg-green-200"
              >
                <Plus className="w-4 h-4" /> Add Hymn
              </button>
            )}
          </div>
        </div>

        {/* Add Hymn Form */}
        <AnimatePresence>
          {showHymnForm && isMainAdmin && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="p-4 bg-green-50 border-b border-green-100">
              <div className="grid md:grid-cols-3 gap-3 mb-3">
                <input type="number" placeholder="Hymn Number *" className="border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-400 outline-none" value={hymnDraft.number} onChange={e => setHymnDraft(d => ({...d, number: e.target.value}))} />
                <input placeholder="Title *" className="border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-400 outline-none" value={hymnDraft.title} onChange={e => setHymnDraft(d => ({...d, title: e.target.value}))} />
                <select className="border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-400 outline-none" value={hymnDraft.language} onChange={e => setHymnDraft(d => ({...d, language: e.target.value}))}>
                  <option value="english">English</option>
                  <option value="yoruba">Yorùbá</option>
                </select>
              </div>
              <input placeholder="First Line" className="w-full border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-400 outline-none mb-3" value={hymnDraft.first_line} onChange={e => setHymnDraft(d => ({...d, first_line: e.target.value}))} />
              <textarea placeholder="Verses (separate with blank line)" className="w-full border-2 border-green-200 rounded-xl px-4 py-2 focus:border-green-400 outline-none mb-3" rows={4} value={hymnDraft.verses} onChange={e => setHymnDraft(d => ({...d, verses: e.target.value}))} />
              <div className="flex gap-2">
                <button onClick={onSaveHymn} className="px-4 py-2 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700">Save Hymn</button>
                <button onClick={() => setShowHymnForm(false)} className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 font-medium">Cancel</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hymn List */}
        <div className="max-h-[500px] overflow-y-auto">
          {hymns.map(hymn => {
            const isSelected = selectedHymns.some(h => h.number === hymn.number);
            const canSelect = selectedHymns.length < requiredHymnCount || isSelected;
            const key = `${hymn.number}-${hymn.language}`;
            const isEditing = editingKey === key;
            if (isEditing) {
              return (
                <div key={key} className="p-4 border-b border-slate-100 bg-amber-50/60" data-testid={`hymn-edit-${hymn.number}-${hymn.language}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold bg-amber-500 text-white">
                      {hymn.number}
                    </div>
                    <span className="text-xs font-medium uppercase tracking-wider text-amber-700">
                      Editing · {hymn.language}
                    </span>
                    <div className="flex-1" />
                    <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) runOcrOnImage(f);
                        e.target.value = '';
                      }}
                      data-testid="hymn-ocr-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      disabled={ocrLoading}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                      title="Take or upload a photo of the hymn — AI will read it and fill the form"
                      data-testid="hymn-ocr-upload-btn"
                    >
                      {ocrLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Reading photo…</>
                      ) : (
                        <><Camera className="w-4 h-4" /> Upload photo</>
                      )}
                    </button>
                  </div>
                  {ocrError && (
                    <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="hymn-ocr-error">
                      {ocrError}
                    </div>
                  )}
                  <input
                    placeholder="Title *"
                    className="w-full border-2 border-amber-200 rounded-xl px-4 py-2 mb-2 focus:border-amber-400 outline-none"
                    value={editDraft.title}
                    onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                    data-testid="edit-hymn-title"
                  />
                  <input
                    placeholder="First Line"
                    className="w-full border-2 border-amber-200 rounded-xl px-4 py-2 mb-2 focus:border-amber-400 outline-none"
                    value={editDraft.first_line}
                    onChange={e => setEditDraft(d => ({ ...d, first_line: e.target.value }))}
                    data-testid="edit-hymn-first-line"
                  />
                  <textarea
                    placeholder="Verses (separate stanzas with a blank line)"
                    className="w-full border-2 border-amber-200 rounded-xl px-4 py-2 mb-3 focus:border-amber-400 outline-none font-mono text-sm"
                    rows={8}
                    value={editDraft.verses}
                    onChange={e => setEditDraft(d => ({ ...d, verses: e.target.value }))}
                    data-testid="edit-hymn-verses"
                  />
                  <p className="text-xs text-slate-500 mb-3">
                    Tip: one stanza per block, separated by a blank line. Each block becomes its own slide.
                    Use <strong>Upload photo</strong> to snap the hymn from paper and auto-fill the fields.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(hymn)}
                      disabled={editSaving}
                      className="px-4 py-2 rounded-xl bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                      data-testid="save-hymn-edit"
                    >
                      {editSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 font-medium"
                      data-testid="cancel-hymn-edit"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={key} className={`p-4 border-b border-slate-50 transition-all ${isSelected ? 'bg-purple-50 border-l-4 border-l-purple-500' : canSelect ? 'hover:bg-slate-50' : 'opacity-50'}`}>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => canSelect && onToggleHymnSelection(hymn)}
                    disabled={!canSelect}
                    className="flex items-center gap-4 flex-1 min-w-0 text-left disabled:cursor-not-allowed"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold shrink-0 ${isSelected ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {hymn.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-800 truncate">{hymn.title}</h4>
                      <p className="text-sm text-slate-500 truncate">{hymn.first_line}</p>
                    </div>
                    {isSelected && <div className="p-2 rounded-full bg-purple-500 text-white shrink-0"><Check className="w-4 h-4" /></div>}
                  </button>
                  {isMainAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); startEdit(hymn); }}
                        title="Edit hymn"
                        className="p-2 rounded-lg text-amber-600 hover:bg-amber-100"
                        data-testid={`edit-hymn-btn-${hymn.number}-${hymn.language}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteHymn(hymn); }}
                        title="Delete hymn"
                        className="p-2 rounded-lg text-red-600 hover:bg-red-100"
                        data-testid={`delete-hymn-btn-${hymn.number}-${hymn.language}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 disabled:opacity-50">
            <ChevronLeft className="w-4 h-4 inline" /> Prev
          </button>
          <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 disabled:opacity-50">
            Next <ChevronRight className="w-4 h-4 inline" />
          </button>
        </div>
      </div>

      {/* Selection Panel */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-500" /> Service Selection
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-600 mb-2">Service Type</label>
          <div className="space-y-2">
            {serviceTypes.map(type => (
              <button key={type.value} onClick={() => { setServiceType(type.value); setSelectedHymns([]); }} className={`w-full p-3 rounded-xl border-2 text-left transition-all ${serviceType === type.value ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <p className="font-medium text-slate-800">{type.label}</p>
                <p className="text-xs text-slate-500">{type.hymnCount} hymns required</p>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-600 mb-2">Service Date</label>
          <input type="date" value={serviceDate} onChange={e => setServiceDate(e.target.value)} className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none" />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-600 mb-2">Selected ({selectedHymns.length}/{requiredHymnCount})</label>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {selectedHymns.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Click on hymns to select</p>
            ) : (
              selectedHymns.map((hymn, idx) => (
                <div key={hymn.number} className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                  <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center">{idx + 1}</span>
                  <span className="flex-1 text-sm font-medium text-slate-700 truncate">Hymn No {hymn.number} - {hymn.title}</span>
                  <button onClick={() => setSelectedHymns(selectedHymns.filter(h => h.number !== hymn.number))} className="p-1 rounded hover:bg-red-100 text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <button onClick={onSaveHymnSelection} disabled={saving || selectedHymns.length !== requiredHymnCount || !serviceDate} className={`w-full py-3 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${selectedHymns.length === requiredHymnCount && serviceDate ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-300 cursor-not-allowed'}`}>
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Selection
        </button>
      </div>
    </div>
  );
}
